import { Link } from "@tanstack/react-router";
import { ArrowRight, CheckCircle2, MapPin, Phone } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { HeroBackground } from "@/components/HeroBackground";
import { Button } from "@/components/ui/button";
import { company } from "@/data/site";
import { getServiceHeroImage } from "@/data/serviceHeroImages";
import type { CaCityLocal } from "@/data/caCityLocal";
import { caCityLocal } from "@/data/caCityLocal";
import type { RoofingServiceContent } from "@/components/RoofingServicePage";
import { roofingTiles, solarTiles } from "@/data/serviceTiles";
import g1 from "@/assets/gallery-new/6940e3a896e7535d5af6346b-2.webp.asset.json";
import g2 from "@/assets/gallery-new/6940e3a896e753e8e1f6346a-2.webp.asset.json";
import g3 from "@/assets/gallery-new/6940e3a8ab25944693304115-2.webp.asset.json";
import g4 from "@/assets/gallery-new/6940e3a8ab25949211304117-2.webp.asset.json";
import g5 from "@/assets/gallery-new/6940e3a8b4f42026887ca890-2.webp.asset.json";
import g6 from "@/assets/gallery-new/6940e3a8b4f420921f7ca893-2.webp.asset.json";
import g7 from "@/assets/gallery-new/6940e3a8ca729802452657d2-2.webp.asset.json";
import g8 from "@/assets/gallery-new/6940e3a997c0231b045981a5-2.webp.asset.json";
import g9 from "@/assets/gallery-new/6940e3a997c0238f425981a9-2.webp.asset.json";
import mRoofRepair from "@/assets/mckay/roof-repair.webp.asset.json";
import mRoofReplacement from "@/assets/mckay/roof-replacement.webp.asset.json";
import mRoofingService from "@/assets/mckay/roofing-service.webp.asset.json";
import mSolarEnergy from "@/assets/mckay/solar-energy.webp.asset.json";
import mSolarInstall from "@/assets/mckay/solar-install.webp.asset.json";

const roofingGallery = [mRoofRepair.url, g1.url, mRoofReplacement.url, g3.url, mRoofingService.url, g5.url, g7.url, g6.url];
const solarGallery = [mSolarInstall.url, g2.url, mSolarEnergy.url, g4.url, g6.url, g8.url, g9.url, g1.url];

type Props = {
  city: CaCityLocal;
  serviceSlug: string;
  serviceTitle: string;
  category: "roofing" | "solar";
  base: RoofingServiceContent;
};

function CtaPair() {
  return (
    <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
      <Button asChild className="btn-glow bg-accent text-accent-foreground hover:bg-accent/90 shadow-glow">
        <Link to="/contact">Get a Free Quote <ArrowRight className="ml-1 h-4 w-4" /></Link>
      </Button>
      <Button asChild className="btn-glow bg-primary text-primary-foreground hover:bg-primary/90 shadow-glow">
        <Link to="/contact"><Phone className="mr-2 h-4 w-4" />{company.phone}</Link>
      </Button>
    </div>
  );
}

function QuoteButton({ label = "Get a Free Quote" }: { label?: string }) {
  return (
    <Button asChild size="sm" className="btn-glow mt-6 bg-accent text-accent-foreground hover:bg-accent/90 shadow-glow">
      <Link to="/contact">{label} <ArrowRight className="ml-1 h-4 w-4" /></Link>
    </Button>
  );
}

function SectionImage({ src, alt }: { src: string; alt: string }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-muted shadow-sm">
      <img src={src} alt={alt} loading="lazy" className="h-full w-full object-cover" />
    </div>
  );
}

export function CityServicePage({ city, serviceSlug, serviceTitle, category, base }: Props) {
  const heroImage = getServiceHeroImage(serviceSlug);
  const pool = category === "solar" ? solarTiles : roofingTiles;
  const otherServices = pool.filter((t) => t.slug !== serviceSlug).slice(0, 8);
  const crossPool = category === "solar" ? roofingTiles : solarTiles;
  const crossServices = crossPool.slice(0, 4);
  const nearby = city.nearby.map((s) => caCityLocal[s]).filter(Boolean);
  const hoods = city.neighborhoods.slice(0, 4).join(", ");
  const gallery = category === "solar" ? solarGallery : roofingGallery;

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="flex-1">
        {/* Hero */}
        <section className="relative min-h-[80vh] w-full overflow-hidden bg-primary text-primary-foreground">
          {heroImage && <HeroBackground src={heroImage} alt={`${serviceTitle} in ${city.shortName}, California`} overlay="black-soft" />}
          <div className="relative mx-auto flex min-h-[80vh] w-full max-w-7xl items-center px-4 py-24 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <div className="inline-flex w-fit items-center gap-2 rounded-full border border-primary-foreground/30 bg-white/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] backdrop-blur">
                <MapPin className="h-3.5 w-3.5" /> {city.shortName}, CA
              </div>
              <h1 className="mt-6 text-4xl font-bold leading-[1.1] sm:text-5xl lg:text-6xl text-balance">
                {serviceTitle} in {city.shortName}
              </h1>
              <p className="mt-6 max-w-2xl text-base text-primary-foreground/85 sm:text-lg">
                Local {serviceTitle.toLowerCase()} for {city.shortName} homeowners – from a licensed Southern California crew that has been on roofs here since 1981.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Button asChild size="lg" className="btn-glow bg-primary text-primary-foreground hover:bg-primary/90 shadow-glow">
                  <Link to="/contact">Get a Free {city.shortName} Quote <ArrowRight className="ml-1 h-4 w-4" /></Link>
                </Button>
                <Button asChild size="lg" className="btn-glow bg-accent text-accent-foreground hover:bg-accent/90 shadow-glow">
                  <Link to="/contact"><Phone className="mr-2 h-4 w-4" />{company.phone}</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Localized intro */}
        <section className="bg-background">
          <div className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-10 px-4 py-14 sm:px-6 sm:py-16 lg:grid-cols-2 lg:px-8">
            <div>
              <h2 className="text-3xl font-bold text-primary sm:text-4xl text-balance">
                {serviceTitle} built for {city.shortName} homes
              </h2>
              <div className="mt-5 space-y-4 text-base leading-relaxed text-foreground/85 sm:text-lg">
                <p>{city.housing}</p>
                <p>{base.intro}</p>
                <p>
                  We work all over {city.shortName}, including {hoods}, and we know the streets around {city.landmarks.slice(0, 2).join(" and ")} well enough to tell you what usually goes wrong on a roof like yours before we get up there.
                </p>
              </div>
              <CtaPair />
            </div>
            <SectionImage src={gallery[0]} alt={`${serviceTitle} crew working in ${city.shortName}, California`} />
          </div>
        </section>

        {/* Local conditions */}
        <section className="bg-muted/40">
          <div className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-10 px-4 py-14 sm:px-6 sm:py-16 lg:grid-cols-2 lg:px-8">
            <div className="lg:order-first">
              <SectionImage src={gallery[2]} alt={`Roof conditions typical of ${city.shortName}, CA homes`} />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-primary sm:text-4xl text-balance">
                What {city.shortName} weather does to roofs
              </h2>
              <div className="mt-5 space-y-4 text-base leading-relaxed text-foreground/85 sm:text-lg">
                <p>{city.climate}</p>
                <p>{category === "solar" ? city.solar : city.permits}</p>
              </div>
              <ul className="mt-6 space-y-3 text-base text-foreground/85">
                {city.neighborhoods.map((n) => (
                  <li key={n} className="flex items-start gap-3">
                    <CheckCircle2 className="mt-1 h-5 w-5 shrink-0 text-secondary" />
                    <span>{serviceTitle} available throughout {n}</span>
                  </li>
                ))}
              </ul>
              <QuoteButton label={`Get a Free ${city.shortName} Quote`} />
            </div>
          </div>
        </section>

        {/* Base service sections, alternating with imagery */}
        <section className="bg-background">
          <div className="mx-auto max-w-6xl space-y-16 px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
            {base.sections.map((s, i) => (
              <div key={s.title} className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
                <article className={i % 2 === 1 ? "lg:order-last" : undefined}>
                  <h2 className="text-3xl font-bold text-primary sm:text-4xl text-balance">
                    {s.title} <span className="text-secondary">in {city.shortName}</span>
                  </h2>
                  <div className="mt-5 space-y-4 text-base leading-relaxed text-foreground/85 sm:text-lg">
                    {s.body.map((p, pi) => (
                      <p key={pi}>{p}</p>
                    ))}
                    {s.bullets && s.bullets.length > 0 && (
                      <ul className="mt-4 space-y-3">
                        {s.bullets.map((b) => (
                          <li key={b} className="flex items-start gap-3">
                            <CheckCircle2 className="mt-1 h-5 w-5 shrink-0 text-secondary" />
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {(!s.bullets || s.bullets.length === 0) && (
                      <ul className="mt-4 space-y-3">
                        {[
                          `${serviceTitle} handled by our own ${city.shortName} crew – no subcontractors`,
                          `Licensed, insured and permitted for work in ${city.shortName}, CA`,
                          `Written scope and pricing before anything starts`,
                          `Clean-up and haul-away included on every ${city.shortName} job`,
                        ].map((b) => (
                          <li key={b} className="flex items-start gap-3">
                            <CheckCircle2 className="mt-1 h-5 w-5 shrink-0 text-secondary" />
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <QuoteButton label={`Get a Free ${city.shortName} Quote`} />
                </article>
                <SectionImage src={gallery[(i + 4) % gallery.length]} alt={`${s.title} – ${serviceTitle.toLowerCase()} in ${city.shortName}, CA`} />
              </div>
            ))}
          </div>
        </section>

        {/* Permits / solar note */}
        <section className="bg-primary text-primary-foreground">
          <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
            <h2 className="text-3xl font-bold sm:text-4xl text-balance">Permits and inspections in {city.shortName}</h2>
            <div className="mt-5 space-y-4 text-base leading-relaxed text-primary-foreground/85 sm:text-lg">
              <p>{city.permits}</p>
              <p>{category === "solar" ? city.solar : `If your ${city.shortName} home already has panels, we can detach and reset the array so the roof underneath gets done properly – see `}
                {category === "roofing" && (
                  <Link to="/services/solar-panel-removal-reinstallation" className="font-semibold text-accent underline underline-offset-4">
                    solar panel removal and reinstallation
                  </Link>
                )}
                {category === "roofing" ? "." : ""}
              </p>
            </div>
            <div className="mt-7 flex flex-wrap gap-3">
              <Button asChild size="lg" className="btn-glow bg-accent text-accent-foreground hover:bg-accent/90 shadow-glow">
                <Link to="/contact">Get a Free Quote <ArrowRight className="ml-1 h-4 w-4" /></Link>
              </Button>
              <Button asChild size="lg" className="btn-glow bg-white/10 text-primary-foreground hover:bg-white/20 shadow-glow">
                <Link to="/contact"><Phone className="mr-2 h-4 w-4" />{company.phone}</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Other services in this city */}
        <section className="bg-muted/40">
          <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
            <h2 className="text-2xl font-bold text-primary sm:text-3xl text-balance">
              More {category === "solar" ? "solar" : "roofing"} services in {city.shortName}
            </h2>
            <p className="mt-2 text-muted-foreground">Every service below is available in {city.shortName} with the same crew and the same standards.</p>
            <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {otherServices.map((t) => (
                <Link
                  key={t.slug}
                  to="/areas/california/$city/$service"
                  params={{ city: city.slug, service: t.slug }}
                  className="group flex items-center justify-between gap-2 rounded-xl border border-border bg-card px-4 py-3 text-sm font-semibold text-primary transition-all hover:-translate-y-0.5 hover:border-primary hover:shadow-md"
                >
                  <span>{t.title} in {city.shortName}</span>
                  <ArrowRight className="h-4 w-4 shrink-0 text-secondary transition-transform group-hover:translate-x-1" />
                </Link>
              ))}
            </div>

            <h3 className="mt-12 text-xl font-bold text-primary sm:text-2xl">
              Also popular with {city.shortName} homeowners
            </h3>
            <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {crossServices.map((t) => (
                <Link
                  key={t.slug}
                  to="/areas/california/$city/$service"
                  params={{ city: city.slug, service: t.slug }}
                  className="group flex items-center justify-between gap-2 rounded-xl border border-border bg-card px-4 py-3 text-sm font-semibold text-primary transition-all hover:-translate-y-0.5 hover:border-secondary hover:shadow-md"
                >
                  <span>{t.title} in {city.shortName}</span>
                  <ArrowRight className="h-4 w-4 shrink-0 text-secondary transition-transform group-hover:translate-x-1" />
                </Link>
              ))}
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Button asChild variant="outline" className="btn-lift border-primary/30 bg-transparent text-primary hover:bg-primary hover:text-primary-foreground">
                <Link to="/areas/california/$city" params={{ city: city.slug }}>
                  Back to {city.shortName} overview
                </Link>
              </Button>
              <Button asChild variant="outline" className="btn-lift border-primary/30 bg-transparent text-primary hover:bg-primary hover:text-primary-foreground">
                <Link to={category === "solar" ? "/services/solar" : "/services/roofing"}>
                  All {category === "solar" ? "solar" : "roofing"} services
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Nearby cities */}
        <section className="bg-background">
          <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-primary sm:text-3xl">{serviceTitle} near {city.shortName}</h2>
            <p className="mt-2 text-muted-foreground">McKay also serves these neighboring cities across Southern California.</p>
            <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {nearby.map((n) => (
                <Link
                  key={n.slug}
                  to="/areas/california/$city/$service"
                  params={{ city: n.slug, service: serviceSlug }}
                  className="group flex items-center justify-between rounded-xl border border-border bg-card px-4 py-3 text-sm font-semibold text-primary shadow-sm transition-all hover:-translate-y-0.5 hover:border-secondary hover:text-secondary hover:shadow-md"
                >
                  <span>{serviceTitle} in {n.shortName}</span>
                  <ArrowRight className="h-4 w-4 opacity-0 transition-all group-hover:translate-x-0.5 group-hover:opacity-100" />
                </Link>
              ))}
              <Link
                to="/areas-served"
                className="group flex items-center justify-between rounded-xl border border-primary bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground shadow-sm transition-all hover:-translate-y-0.5 hover:bg-primary/90 hover:shadow-md"
              >
                <span>View Areas</span>
                <ArrowRight className="h-4 w-4 transition-all group-hover:translate-x-0.5" />
              </Link>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="bg-white text-primary">
          <div className="mx-auto max-w-5xl px-4 py-16 text-center sm:px-6 lg:px-8">
            <h2 className="text-3xl font-extrabold sm:text-4xl text-balance">
              Ready for a free {serviceTitle.toLowerCase()} quote in {city.shortName}?
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-primary/80">
              Tell us what's going on with the roof. We usually respond the same day, often within the hour.
            </p>
            <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Button asChild size="lg" className="btn-glow bg-accent text-accent-foreground hover:bg-accent/90 shadow-glow">
                <Link to="/contact">Get a Free Quote</Link>
              </Button>
              <Button asChild size="lg" className="btn-glow bg-primary text-primary-foreground hover:bg-primary/90 shadow-glow">
                <Link to="/contact"><Phone className="mr-2 h-4 w-4" />{company.phone}</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
